import { IoTriangleSharp } from "react-icons/io5";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../database/config";
import { useHomeData } from "../../database/HomeDataContext";
import { HeroSkeleton } from "../Skeletons/Skeletons";
import DownloadAppModal from "../Modals/DownloadAppModal";
import "./Hero.css";

const Hero = () => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fallback fetch for when used outside HomeDataProvider
  useEffect(() => {
    if (!isOutsideProvider) return;

    const fetchHeroData = async () => {
      try {
        const docRef = doc(db, "home", "body1");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const d = docSnap.data();
          setLocalData({
            topOrangeBar: d.topOrangeBar || '',
            whiteHeading: d.whiteHeading || '',
            OrangeHeading: d.OrangeHeading || '',
            smallHeading: d.smallHeading || '',
            imgLaptop: d.imgLaptop || '',
            mobileImageBg: d.mobileImageBg || '',
            imgAlt: d.imgAlt || '',
            imgTitle: d.imgTitle || '',
            button1: d.button1 || '',
            button2: d.button2 || ''
          });
        }
      } catch (error) {
        console.error("Error fetching hero data:", error);
      }
    };
    fetchHeroData();
  }, [isOutsideProvider]);

  const heroData = isOutsideProvider ? localData : homeData?.hero;

  // Show skeleton while loading
  if (!heroData) {
    return <HeroSkeleton />;
  }

  const handleHowItWorks = (e) => {
    e.preventDefault();
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero-section relative min-h-[100svh] overflow-hidden bg-slate-900">
      {/* Background Image - Desktop (hidden on mobile) */}
      {heroData.imgLaptop && (
        <img
          src={heroData.imgLaptop}
          alt={heroData.imgAlt}
          title={heroData.imgTitle}
          fetchPriority="high"
          className="hero-background-desktop absolute inset-0 w-full h-full hidden sm:block object-cover object-center opacity-80"
        />
      )}

      {/* Background Image - Mobile (visible only on mobile) */}
      {heroData.mobileImageBg && (
        <img
          src={heroData.mobileImageBg}
          alt={heroData.imgAlt}
          title={heroData.imgTitle}
          fetchPriority="high"
          className="hero-background-mobile absolute inset-0 w-full h-full block sm:hidden object-cover object-center opacity-80"
        />
      )}

      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Content Container */}
      <div className="relative z-10 min-h-[100svh] flex flex-col justify-center items-start px-6 sm:pl-16 md:pl-24 lg:pl-32 xl:pl-48 pr-6 pt-32 pb-10 md:pt-24 md:pb-12 transition-opacity duration-700">
        <div className="animate-fade-in-up">
          {/* Top Orange Bar */}
          <div className="hero-orange-bar inline-flex items-center bg-orange-500/40 text-white px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium mb-4 sm:mb-8 backdrop-blur-sm">
            <span>{heroData.topOrangeBar}</span>
          </div>

          {/* Main Heading */}
          <h1 className="hero-main-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight mb-3 sm:mb-6">
            <span className="text-white italic">{heroData.whiteHeading}</span>{" "}
            <span className="text-orange-500 italic">{heroData.OrangeHeading}</span>
          </h1>

          {/* Small Heading / Subtext */}
          <p className="hero-small-heading text-white/90 text-sm sm:text-base md:text-xl lg:text-2xl max-w-2xl leading-relaxed italic text-left">
            {heroData.smallHeading}
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mt-6 sm:mt-8">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-lg sm:text-xl lg:text-2xl font-medium transition-all duration-200 hover:shadow-lg"
            >
              {heroData.button1 || 'Order Now'}
            </button>

            {/* <button
              onClick={() => setIsModalOpen(true)}
              className="group relative overflow-hidden bg-orange-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-lg sm:text-xl lg:text-2xl font-medium transition-shadow duration-300 hover:shadow-lg"
            >
              <span className="relative z-10">{heroData.button1 || 'Order Now'}</span>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-0 h-0 bg-black rounded-full transition-all duration-1000 ease-in-out delay-0 group-hover:delay-[400ms] group-hover:w-[300px] group-hover:h-[300px] md:group-hover:w-[400px] md:group-hover:h-[400px]"></div>
            </button> */}

            <Link
              to="/"
              state={{ scrollTo: 'how-it-works' }}
              onClick={handleHowItWorks}
              className="flex items-center gap-2 text-white"
            >
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center">
                <span className="relative z-10">
                  <IoTriangleSharp className="rotate-90 text-lg sm:text-xl" />
                </span>
                <span className="absolute inset-0 rounded-full border-4 border-orange-500 border-r-transparent" />
              </div>
              <p className="text-sm sm:text-base lg:text-lg font-medium">{heroData.button2 || 'How it works?'}</p>
            </Link>
          </div>
        </div>
      </div>

      <DownloadAppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Hero;
